import {
  NetworkIcon,
  Users,
  Plug,
  Puzzle,
  FileCode,
  Webhook,
  FolderGit2,
  type LucideIcon,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMemo, type ReactElement, type ReactNode } from 'react';
import { PostureManagementErrorBoundary } from './components/PostureManagementErrorBoundary';
import type { PostureManagementTabType } from './types';
import { PostureManagementTabs } from './constants';
import { NoTabs } from './components/NoTabs';
import { DataTableUsersContent } from '@/pages/organization/tabs/DataTableUsersTab';
import { DataTableProjectsContent } from '@/pages/organization/tabs/DataTableProjectsTab';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import { useInventoryTabManagement } from '@/hooks/useInventoryTabManagement';
import DataTableAiInstructionsTab from '@/pages/dashboard/tabs/DataTableAiInstructionsTab';
import DataTableHooksTab from '@/pages/dashboard/tabs/DataTableHooksTab';

interface PostureManagementProps {
  organizationId?: string;
  /** KIR-2917 — gated by HOOK_INVENTORY_ENABLED on the caller. */
  hookInventoryAllowed?: boolean;
  projectsInventoryAllowed?: boolean;
  /**
   * Whether the user may view org-wide (admin) data. True for owner, manager,
   * and support. Optional: if provided by the parent, avoids a duplicate role
   * check; otherwise computed internally with a loading gate.
   */
  canViewAdminContent?: boolean;
  MCPTabSlot: ReactNode;
  ExtensionsTabSlot?: ReactNode;
  PluginsTabSlot?: ReactNode;
}

interface PostureManagementTabDetails {
  label: string;
  icon: LucideIcon;
  Component: ReactNode;
  guard?: () => boolean;
  /** Optional pill rendered next to the label (e.g. a "NEW" marker). */
  badge?: ReactNode;
}

interface CreateTabsConfigOptions {
  organizationId?: string;
  canViewAdminContent: boolean;
  hookInventoryAllowed?: boolean;
  projectsInventoryAllowed?: boolean;
  slots: {
    MCPTab: PostureManagementProps['MCPTabSlot'];
    ExtensionsTab?: PostureManagementProps['ExtensionsTabSlot'];
    PluginsTab?: PostureManagementProps['PluginsTabSlot'];
  };
}

type PostureManagementTabConfig = Record<
  PostureManagementTabType,
  PostureManagementTabDetails
>;

const createTabsTrigger = (
  tabsConfig: Record<PostureManagementTabType, PostureManagementTabDetails>
) => {
  return Object.entries(tabsConfig).map(([tabKey, config]) => {
    const showTab = config.guard?.() ?? true;
    if (!showTab) {
      return null;
    }
    return (
      <TabsTrigger
        className="flex items-center justify-center gap-1 !whitespace-normal sm:!whitespace-nowrap text-xs sm:text-sm h-auto sm:h-[calc(100%-1px)] py-2 sm:py-1"
        value={tabKey}
        key={tabKey}
      >
        <config.icon className="size-5 shrink-0" />
        <span className="break-words text-center">{config.label}</span>
        {config.badge}
      </TabsTrigger>
    );
  });
};

const createTabsContent = (
  tabsConfig: Record<PostureManagementTabType, PostureManagementTabDetails>
) => {
  return Object.entries(tabsConfig).map(([tabKey, config]) => {
    const showTab = config.guard?.() ?? true;
    if (!showTab) {
      return null;
    }
    return (
      <TabsContent
        value={tabKey}
        key={tabKey}
        className="flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        <PostureManagementErrorBoundary
          tab={tabKey as PostureManagementTabType}
        >
          {config.Component}
        </PostureManagementErrorBoundary>
      </TabsContent>
    );
  });
};

const createTabsConfig = ({
  organizationId,
  canViewAdminContent,
  hookInventoryAllowed,
  projectsInventoryAllowed,
  slots: { MCPTab, ExtensionsTab, PluginsTab },
}: CreateTabsConfigOptions): PostureManagementTabConfig => {
  return {
    // User Security Posture tab is first (as per design doc)
    // Only visible to owner/manager/support in an organization context
    [PostureManagementTabs.USER_SECURITY_POSTURE]: {
      label: 'Users',
      icon: Users,
      Component: organizationId ? (
        <DataTableUsersContent organizationId={organizationId} />
      ) : null,
      guard: () => !!organizationId && canViewAdminContent,
    },
    // Projects tab is 2nd (after Users), gated by the projects_inventory flag.
    [PostureManagementTabs.PROJECTS]: {
      label: 'Projects',
      icon: FolderGit2,
      Component: organizationId ? (
        <DataTableProjectsContent organizationId={organizationId} />
      ) : null,
      guard: () =>
        !!organizationId && !!projectsInventoryAllowed && canViewAdminContent,
    },
    [PostureManagementTabs.MCP]: {
      label: 'MCP Servers',
      icon: NetworkIcon,
      Component: MCPTab,
      guard: () => MCPTab !== null,
    },
    [PostureManagementTabs.EXTENSIONS]: {
      label: 'Extensions',
      icon: Plug,
      Component: ExtensionsTab,
      guard: () => ExtensionsTab != null,
    },
    [PostureManagementTabs.PLUGINS]: {
      label: 'Plugins',
      icon: Puzzle,
      Component: PluginsTab,
      guard: () => PluginsTab != null,
    },
    [PostureManagementTabs.AI_INSTRUCTIONS]: {
      label: 'AI Instructions',
      icon: FileCode,
      Component: organizationId ? (
        <DataTableAiInstructionsTab organizationId={organizationId} />
      ) : null,
      guard: () => !!organizationId && canViewAdminContent,
    },
    // KIR-2917 / KIR-3643 — Hooks tab: shown in the org view (owner/manager/
    // support, scoped to the org) and in the personal dashboard (the caller's
    // own org-less hooks). Gated by HOOK_INVENTORY_ENABLED in both. Sits to the
    // right of AI Instructions per spec.
    [PostureManagementTabs.HOOKS]: {
      label: 'Hooks',
      icon: Webhook,
      badge: (
        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-blue-700">
          New
        </span>
      ),
      Component: organizationId ? (
        <DataTableHooksTab scope={{ kind: 'organization', organizationId }} />
      ) : (
        <DataTableHooksTab scope={{ kind: 'account' }} />
      ),
      // Personal scope has no org role gate; org scope gates on the read role
      // (owner/manager/support). Keep the ternary explicit rather than relying
      // on useRoleCheck returning `true` when there is no organizationId.
      guard: () =>
        !!hookInventoryAllowed && (organizationId ? canViewAdminContent : true),
    },
  };
};

export const PostureManagement = ({
  organizationId,
  canViewAdminContent: canViewAdminContentProp,
  hookInventoryAllowed,
  projectsInventoryAllowed,
  MCPTabSlot,
  ExtensionsTabSlot,
  PluginsTabSlot,
}: PostureManagementProps): ReactElement | null => {
  const isHookInventoryEnabled = hookInventoryAllowed ?? false;

  // One read-scope role check (owner/manager/support), falling back to an
  // internal hook when the parent doesn't pass the prop (e.g. Dashboard,
  // tests). It gates Users, Projects, AI Instructions, and Hooks — support
  // reads every org surface (KIR-4128).
  // When the parent already supplies the role, pass `undefined` to the
  // internal hook so its underlying useRoleCheck query stays disabled — the
  // hook still runs (rules-of-hooks) but does no network work.
  const adminContentCheck = useCanViewAdminContent(
    canViewAdminContentProp === undefined ? organizationId : undefined
  );
  const canViewAdminContent =
    canViewAdminContentProp ?? adminContentCheck.canViewAdminContent;

  // Gate render until an internally-resolved role check finishes, so the
  // default-tab selection doesn't race role data.
  const needsLoadingGate =
    canViewAdminContentProp === undefined && adminContentCheck.isLoading;

  const tabsConfig = createTabsConfig({
    organizationId,
    canViewAdminContent,
    hookInventoryAllowed: isHookInventoryEnabled,
    projectsInventoryAllowed: projectsInventoryAllowed ?? false,
    slots: {
      MCPTab: MCPTabSlot,
      ExtensionsTab: ExtensionsTabSlot,
      PluginsTab: PluginsTabSlot,
    },
  });

  // Manage tab state and URL synchronization
  const { currentTab, handleTabChange } = useInventoryTabManagement({
    tabsConfig,
  });

  const triggers = createTabsTrigger(tabsConfig);
  const contents = createTabsContent(tabsConfig);

  const visibleTabCount = useMemo(
    () =>
      Object.values(tabsConfig).filter(config => config.guard?.() ?? true)
        .length,
    [tabsConfig]
  );
  const hasTabs = visibleTabCount > 0;
  const tabsListGridCols =
    visibleTabCount === 1
      ? 'sm:grid-cols-1'
      : visibleTabCount === 2
        ? 'sm:grid-cols-2'
        : visibleTabCount === 3
          ? 'sm:grid-cols-3'
          : visibleTabCount === 4
            ? 'sm:grid-cols-4'
            : visibleTabCount === 5
              ? 'sm:grid-cols-5'
              : visibleTabCount === 6
                ? 'sm:grid-cols-6'
                : visibleTabCount === 7
                  ? 'sm:grid-cols-7'
                  : 'sm:grid-cols-8';

  // Wait for user data to load before rendering tabs (when using internal role check)
  // This prevents race condition with defaultValue where owner/managers
  // might see the wrong default tab selected before user roles are known
  if (needsLoadingGate) {
    return null;
  }

  // Fallback when no tabs are visible (shouldn't happen if parent checks visibility correctly)
  if (!hasTabs) {
    return <NoTabs />;
  }

  return (
    <Tabs
      value={currentTab}
      onValueChange={handleTabChange}
      className="h-[calc(100vh-11rem)] flex flex-col gap-6"
    >
      <TabsList
        className={`grid w-full grid-cols-1 h-auto sm:h-9 ${tabsListGridCols}`}
      >
        {triggers}
      </TabsList>
      {contents}
    </Tabs>
  );
};
